import PublicPage from "./PublicPage.jsx";
import { PRIVACY_EMAIL, SUPPORT_EMAIL, LEGAL_ENTITY } from "./legalInfo.js";
import CookiePreferencesButton from "./CookiePreferencesButton.jsx";

export default function PrivacyPage({ onNavigate, onGetStarted }) {
  return (
    <PublicPage
      title="Privacy Policy"
      lastUpdated="August 2026"
      onNavigate={onNavigate}
      onGetStarted={onGetStarted}
    >
      <p className="legal-p">
        Simple Expense Management scans receipts and invoices so you can
        track expenses and generate quarterly reports, paying only for the
        scans you use. We are the data controller for personal data
        processed through this service. For any privacy-related queries,
        contact us at <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
      </p>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">1. What we collect and why</h2>
        <p className="legal-p">We collect only what is necessary to run the service:</p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Purpose</th>
                <th>Legal basis</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Email address</td>
                <td>Account login via one-time code</td>
                <td>Needed to provide the service</td>
              </tr>
              <tr>
                <td>Session token (cookie)</td>
                <td>Maintain your authenticated session</td>
                <td>Needed to provide the service; strictly necessary</td>
              </tr>
              <tr>
                <td>Receipt/invoice photos</td>
                <td>Extract vendor, date, amount, VAT and category; attach to your reports</td>
                <td>Needed to provide the service</td>
              </tr>
              <tr>
                <td>Expense records (vendor, amount, date, category)</td>
                <td>Build your expense list and quarterly PDF reports</td>
                <td>Needed to provide the service</td>
              </tr>
              <tr>
                <td>Credit usage records (scans, timestamps)</td>
                <td>Accurate billing, balance deduction, purchase history</td>
                <td>Needed to provide the service; required for financial records</td>
              </tr>
              <tr>
                <td>Payment records</td>
                <td>Fulfil purchase, resolve disputes</td>
                <td>Needed to provide the service; legal obligation</td>
              </tr>
              <tr>
                <td>IP address (rate limiting)</td>
                <td>Prevent abuse of login codes; in-memory only, not persisted to disk</td>
                <td>Our legitimate interest in keeping the service secure</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">2. Receipt images and AI processing</h2>
        <p className="legal-p">
          Each receipt or invoice you scan is sent to a third-party AI
          model (currently Anthropic Claude, accessed via OpenRouter) to
          extract the vendor, date, amount and category. Receipt images
          are also stored privately so they can be attached to your
          quarterly reports and viewed later — they are never shown to
          anyone other than you (and a read-only "shared access" account you
          may have invited).{" "}
          <strong>
            We do not sell your receipt images or use them to train AI
            models.
          </strong>
        </p>
        <p className="legal-p">
          You can delete any expense (and its receipt image) at any time
          from the Expenses tab. Deleting your account removes your
          expenses, receipt images, and settings.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">3. Third-party service providers (sub-processors)</h2>
        <p className="legal-p">The following providers process data on our behalf:</p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>What they process</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>OpenRouter / Anthropic</td>
                <td>Receipt/invoice images, for field extraction only — not retained by us beyond the request</td>
              </tr>
              <tr>
                <td>Cloudflare (R2 storage)</td>
                <td>Receipt image files, stored privately</td>
              </tr>
              <tr>
                <td>Stripe</td>
                <td>Payment card details and billing information — we never see or store your card number</td>
              </tr>
              <tr>
                <td>Amazon Web Services (SES)</td>
                <td>Your email address, to deliver login codes</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="legal-p">
          Some of these providers process data outside the European
          Economic Area. Where such transfers occur, we ensure appropriate
          safeguards are in place under Chapter V GDPR, including Standard
          Contractual Clauses approved by the European Commission.
        </p>
      </section>

      <section className="legal-section" id="cookies">
        <h2 className="landing-h2 legal-h2">4. Cookies</h2>
        <p className="legal-p">
          We use a single strictly-necessary session cookie to keep you
          signed in. It's required for the service to function and can't be
          disabled; no consent is needed for it under Art. 5(3) of the
          ePrivacy Directive. We do not use analytics, advertising, or
          third-party tracking cookies of any kind.
        </p>
        <p className="legal-p">
          <CookiePreferencesButton /> to see the cookie notice again.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">5. Security and breach notification</h2>
        <p className="legal-p">
          We implement appropriate technical and organisational measures to
          protect your personal data, including encryption of data in
          transit (TLS), encrypted storage, access controls limiting who
          can access production data, and activity logging. Passwords are
          not used at all — login is by a one-time code emailed to you,
          which is single-use, expires after 10 minutes, and is capped
          after 5 incorrect attempts.
        </p>
        <p className="legal-p">
          In the event of a personal data breach that is likely to result
          in a risk to your rights and freedoms, we will notify the Belgian
          Data Protection Authority (APD/GBA) within 72 hours of becoming
          aware of the breach (Art. 33 GDPR). Where the breach is likely to
          result in a high risk to you personally, we will also notify you
          directly without undue delay (Art. 34 GDPR).
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">6. Data retention</h2>
        <p className="legal-p">
          <strong>Account data, expenses &amp; receipt images</strong> —
          retained until you delete your account or the individual expense.
          Deletion is immediate and permanent.
        </p>
        <p className="legal-p">
          <strong>Usage &amp; billing records</strong> — retained for 7
          years to comply with Belgian financial record-keeping law (Art.
          III.86 WER / Code de droit économique).
        </p>
        <p className="legal-p">
          <strong>IP addresses</strong> — held in-memory for rate limiting
          only; not written to disk and cleared automatically.
        </p>
        <p className="legal-p">
          You can delete individual expenses at any time, or request full
          account deletion by emailing{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">7. Your rights under GDPR</h2>
        <p className="legal-p">
          If you are in the European Economic Area, you have the following
          rights regarding your personal data:
        </p>
        <ul className="legal-list">
          <li><strong>Access</strong> — request a copy of the data we hold about you.</li>
          <li><strong>Rectification</strong> — ask us to correct inaccurate data.</li>
          <li><strong>Erasure</strong> — ask us to delete your data ("right to be forgotten"). Account deletion fulfils this instantly; billing records subject to legal retention periods cannot be erased early.</li>
          <li><strong>Restriction</strong> — ask us to pause processing while a dispute is resolved.</li>
          <li><strong>Portability</strong> — receive your data in a machine-readable format.</li>
          <li><strong>Objection</strong> — object to processing based on legitimate interest.</li>
        </ul>
        <p className="legal-p">
          To exercise any of these rights, email{" "}
          <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. We will
          respond within 30 days.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">8. Right to complain</h2>
        <p className="legal-p">
          You have the right to lodge a complaint with a supervisory
          authority. In Belgium, this is the Autorité de Protection des
          Données / Gegevensbeschermingsautoriteit (APD/GBA) — see{" "}
          <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer">
            autoriteprotectiondonnees.be
          </a>
          . You may also complain to the supervisory authority in your EU
          member state of residence.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="landing-h2 legal-h2">9. Changes to this policy</h2>
        <p className="legal-p">
          We will update the "last updated" date when this policy changes.
          For material changes that affect your rights, we will notify
          registered users by email at least 14 days before the change
          takes effect.
        </p>
      </section>

      <p className="legal-p">
        <em>Simple Expense Management is a product of {LEGAL_ENTITY}.</em>
      </p>
    </PublicPage>
  );
}
